import { FormEvent, useEffect, useState } from 'react'
import parsePhoneNumber from 'libphonenumber-js'
import * as Toast from '@radix-ui/react-toast'
import { useNavigate } from 'react-router-dom'
import ReactSpinnerTimer from 'react-spinner-timer'
import { useTranslation } from 'react-i18next'
import { PaytrieAPI } from '@paytrie/client'

import { Button } from '@/components/Button/Button'
import {
  ErrorBlockText,
  TextInput,
  LeftAlignSmallText,
} from '@/components/widget-components'
import { verifySMS, sendSMS } from '@/requests/signup'
import { usePageStateStore, useSignupStore } from '@/reducers'
import { useSignupEmail } from '@/hooks/useSignupEmail'
import { submitOnEnter } from '@/utils/keyboard'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
  trackOnboardingVerificationCodeFailed,
  trackOnboardingVerificationCodeResent,
} from '@/utils/analytics'

// POTENTIAL IMPROVEMENT: Extract toast notification into a reusable component
// POTENTIAL IMPROVEMENT: Implement auto-focus on the verification code input when the page loads
// POTENTIAL IMPROVEMENT: Consider adding proper error handling for network issues during SMS verification
export default function SignupContentVerifyPhoneNumber() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [pin, setPhoneNumberPin] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>('')
  const [clicked, setClicked] = useState(false)
  const [codeSentSuccess, setCodeSentSuccess] = useState(true)
  const [open, setOpen] = useState(false)
  const [phoneAlreadyClaimed, setPhoneAlreadyClaimed] = useState(false)
  const email = useSignupEmail()
  const setFormState = useSignupStore.use.nextState()
  const phoneNumber = useSignupStore.use.phoneNumber()
  const clearAuthToken = usePageStateStore.use.clearAuthToken()
  const parsedPhoneNumber = parsePhoneNumber(phoneNumber, 'CA')

  useEffect(() => {
    trackOnboardingStepViewed('sms_code_entry')
  }, [])

  const resendCode = async () => {
    setIsLoading(true)
    trackOnboardingVerificationCodeResent('sms')
    sendSMS(phoneNumber)
      .then(() => {
        setCodeSentSuccess(true)
        setClicked(true)
        setIsLoading(false)
      })
      .catch(() => {
        setCodeSentSuccess(false)
        setClicked(true)
        setIsLoading(false)
        trackOnboardingVerificationCodeFailed('sms', 'resend_request_failed')
      })
  }

  const updateSpinTimer = (lap: any) => {
    if (lap.isFinished) {
      setClicked(false)
    } else {
      if (lap.actualLap === lap.totalLaps) {
        setClicked(false)
      }
    }
  }

  async function sendPhoneNumberPin(e: FormEvent): Promise<void> {
    e.preventDefault()
    trackOnboardingStepSubmitted('sms_code_entry')
    if (pin.length < 4) {
      trackOnboardingStepFailed('sms_code_entry', 'invalid_pin_length')
      return setErrorMessage(t('verifyPhone.error.invalidPin'))
    }
    setIsLoading(true)
    setErrorMessage('')
    const result = await verifySMS(pin)
    if (result.kind === 'success') {
      setFormState()
      void PaytrieAPI.updateCurrentUser({
        currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.VERIFY_PHONE,
      }).catch(() => {})
      trackOnboardingStepCompleted('sms_code_entry')
      navigate('/SignupDisclosure')
    } else if (result.kind === 'phone_already_claimed') {
      setPhoneAlreadyClaimed(true)
      trackOnboardingStepFailed('sms_code_entry', 'phone_already_claimed')
    } else if (
      result.message === 'Failed too many attempts. New SMS code sent'
    ) {
      setErrorMessage(t('verifyPhone.error.tooManyAttempts'))
      trackOnboardingStepFailed('sms_code_entry', 'too_many_attempts')
    } else if (result.message === 'Mismatched verification code') {
      setErrorMessage(t('verifyPhone.error.mismatch'))
      trackOnboardingStepFailed('sms_code_entry', 'code_mismatch')
    } else {
      setErrorMessage(t('verifyPhone.error.unableToProcess'))
      trackOnboardingStepFailed('sms_code_entry', 'unable_to_process')
    }
    setIsLoading(false)
  }

  return (
    <form onSubmit={sendPhoneNumberPin} data-testid="text-phone-form">
      <div className="flex flex-col gap-6">
        <div className="text-lg font-normal uppercase">
          {t('verifyPhone.title')}
        </div>
        <div className="text-base" data-testid="container-instruction">
          {t('verifyPhone.instruction', {
            phoneNumber: parsedPhoneNumber
              ? parsedPhoneNumber.formatNational()
              : '(000) 000-0000',
          })}
        </div>
        <TextInput
          autoFocus
          placeholder={t('verifyPhone.placeholder')}
          type="tel"
          min={0}
          onChange={(e) =>
            setPhoneNumberPin(
              e.target.value.replace(/[^0-9\+]/g, '').substring(0, 4)
            )
          }
          onKeyDown={submitOnEnter(sendPhoneNumberPin)}
          value={pin}
          data-testid="input-phone-verification-code"
        />
        <div className="mb-[-50px]">
          <LeftAlignSmallText className="flex">
            <Toast.Provider swipeDirection="right">
              <button
                className="codeButton"
                type="button"
                style={{ float: 'left', textDecoration: 'underline' }}
                disabled={clicked}
                onClick={() => {
                  setOpen(true)
                  resendCode()
                }}
                data-testid="btn-resend-code"
              >
                {t('verifyPhone.resendCode')}
              </button>
              <Toast.Root
                className="ToastRoot"
                open={open}
                onOpenChange={setOpen}
                data-testid="container-notification"
              >
                {codeSentSuccess && (
                  <Toast.Title
                    className="ToastTitle"
                    data-testid="container-success"
                  >
                    {t('verifyPhone.toast.codeSent')}
                  </Toast.Title>
                )}
                {!codeSentSuccess && (
                  <Toast.Title
                    className="ToastTitle"
                    data-testid="container-error"
                  >
                    {t('verifyPhone.toast.unableToSend')}
                  </Toast.Title>
                )}
              </Toast.Root>
              <Toast.Viewport className="ToastViewport" />
            </Toast.Provider>
            {clicked && (
              <div style={{ scale: '0.5' }} data-testid="container-timer">
                <ReactSpinnerTimer
                  timeInSeconds={60}
                  totalLaps={1}
                  onLapInteraction={updateSpinTimer}
                  isPaused={false}
                />
              </div>
            )}
          </LeftAlignSmallText>
        </div>
        {phoneAlreadyClaimed && (
          <div
            className="rounded-md border border-pebble dark:border-white p-4 text-base"
            data-testid="container-signup-phone-already-claimed"
          >
            <p
              className="mb-4"
              data-testid="text-signup-phone-already-claimed-message"
            >
              {t('signup.phone.error.phoneAlreadyClaimed')}
            </p>
            <Button
              type="button"
              className="inline-flex items-center py-6 px-4 w-full justify-center lg:gap-2"
              onClick={() => {
                clearAuthToken()
                navigate('/EasyLogin')
              }}
              data-testid="btn-signup-phone-go-to-login"
            >
              {t('login.form.submitButton')}
            </Button>
            <p
              className="mt-4 text-sm text-center"
              data-testid="text-signup-phone-already-claimed-support"
            >
              {t('support.needHelp')}{' '}
              <a
                href={`mailto:support@paytrie.com?subject=${encodeURIComponent(
                  t('signup.phone.error.phoneAlreadyClaimedSupportSubject')
                )}&body=${encodeURIComponent(
                  t('signup.phone.error.phoneAlreadyClaimedSupportBody', {
                    email,
                  })
                )}`}
                className="underline"
                data-testid="link-signup-phone-already-claimed-support"
              >
                support@paytrie.com
              </a>
            </p>
          </div>
        )}
        <ErrorBlockText data-testid="error-form-message">
          {errorMessage}
        </ErrorBlockText>
        <Button
          className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
          type="submit"
          disabled={pin.length < 4 || phoneAlreadyClaimed}
          data-testid="btn-continue"
        >
          {t('signup.submit')}
        </Button>
      </div>
    </form>
  )
}
